import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, Grid, Stack, Typography, TextField, IconButton, Avatar, Paper, Badge, Menu, MenuItem, Chip, Button } from "@mui/material";
import { IoIosSend, IoMdImage, IoMdAttach, IoMdArrowBack } from "react-icons/io";
import { MdMoreVert, MdEdit, MdDelete, MdCheck, MdDoneAll } from "react-icons/md";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { getChatUsersListAsync, sendMessageNotification } from "../../services/chatService";
import { incrementUserUnread, clearUserUnread } from "../../store/slices/chat_slice";
import { imgUrl } from "../../utils/imageUrl";
import { themeOrange } from "../../utils/colorTheme";

const socket = io("https://chat-soft-2f178ffd0d28.herokuapp.com");

const SimpleMessages = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.userAuth);
  const { get_all_chats_data, unreadCounts } = useSelector((state) => state.chatManage);

  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [roomId, setRoomId] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [userLastSeen, setUserLastSeen] = useState({}); // Track last activity time
  const [lastMessageTime, setLastMessageTime] = useState({});
  const [recentConversations, setRecentConversations] = useState([]);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [userDataOverrides, setUserDataOverrides] = useState({}); // Store correct user data to override "Customer" placeholders
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const hasAutoSelectedRef = useRef(false);

  // Load users list
  useEffect(() => {
    dispatch(getChatUsersListAsync());
  }, [dispatch]);




  // Auto-select user from navigation state (when navigating from job acceptance)
  useEffect(() => {
    const { id, first_name, last_name, avatar, isNewConversation } = location.state || {};

    // Only auto-select once per session
    if (id && !selectedUser && !hasAutoSelectedRef.current) {
      hasAutoSelectedRef.current = true;

      // Store the correct user data to override any "Customer" placeholders
      if (first_name && first_name !== "Customer") {
        setUserDataOverrides(prev => ({
          ...prev,
          [id]: { first_name, last_name, avatar }
        }));
      }

      // If this is a new conversation, refresh the chat users list
      if (isNewConversation) {
        dispatch(getChatUsersListAsync());
      }

      // Wait for chat list to load/refresh, then try to find the user
      const trySelectUser = (attempt = 1) => {
        // Find the user in the chat list
        let userToSelect = get_all_chats_data?.find(chatUser => Number(chatUser.id) === Number(id));

        if (userToSelect) {
          // Use override data if available
          if (userDataOverrides[id]) {
            userToSelect = {
              ...userToSelect,
              ...userDataOverrides[id]
            };
          }
          handleSelectUser(userToSelect);
        } else if (attempt < 3) {
          // Retry up to 3 times
          setTimeout(() => trySelectUser(attempt + 1), 1000);
        } else {
          // Create user object from navigation state data
          userToSelect = {
            id,
            first_name: first_name || "Customer",
            last_name: last_name || "",
            avatar: avatar || ""
          };
          handleSelectUser(userToSelect);
        }
      };

      // Start trying after a brief initial delay
      setTimeout(() => trySelectUser(), 500);
    }
  }, [location.state, get_all_chats_data, selectedUser, userDataOverrides]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Socket listeners
  useEffect(() => {
    // Set user online and request online users
    if (user?.id) {
      socket.emit("user-online", { userId: user.id, companyId: "66c7dcd95dbdcd5ccb67432a" });

      // Request online users list
      socket.emit("get-online-users", { companyId: "66c7dcd95dbdcd5ccb67432a" });

      // Request last messages for all conversations to populate sorting
      socket.emit("get-last-messages", {
        userId: user.id,
        companyId: "66c7dcd95dbdcd5ccb67432a",
      });
    }

    // Listen for last messages (for sorting only)
    socket.on("last-messages", (data) => {
      if (data && Array.isArray(data)) {
        const times = {};
        data.forEach((msg) => {
          const otherUserId = msg.senderId === user.id ? msg.receiverId : msg.senderId;
          const msgTime = new Date(msg.createdAt).getTime();
          times[otherUserId] = msgTime;
        });
        setLastMessageTime(times);
      }
    });

    // Listen for previous messages
    socket.on("previous-messages", (data) => {
      setMessages(data || []);
      // Users show as offline by default - only real-time activity shows them as online
    });

    // Listen for new messages
    socket.on("receive-message", (data) => {
      setMessages((prev) => {
        // Check if message already exists (prevent duplicates)
        const isDuplicate = prev.some(
          msg => msg.senderId === data.senderId &&
                 msg.message === data.message &&
                 Math.abs(new Date(msg.createdAt) - new Date(data.createdAt)) < 2000
        );
        return isDuplicate ? prev : [...prev, data];
      });

      // Move conversation to top
      const otherUserId = data.senderId === user.id ? data.receiverId : data.senderId;
      moveToTop(otherUserId);

      // Only update last seen if THEY sent the message, not us
      if (data.senderId !== user.id) {
        setUserLastSeen(prev => ({
          ...prev,
          [data.senderId]: Date.now()
        }));
      }

      // If message has sender info, use it to override "Customer" placeholder
      if (data.senderName || data.sender_name || data.user) {
        const senderInfo = data.user || {};
        const senderName = data.senderName || data.sender_name;

        if (senderName && senderName !== "Customer") {
          const nameParts = senderName.split(" ");
          setUserDataOverrides(prev => ({
            ...prev,
            [otherUserId]: {
              first_name: nameParts[0] || senderName,
              last_name: nameParts.slice(1).join(" ") || "",
              avatar: senderInfo.avatar || data.avatar || ""
            }
          }));
        } else if (senderInfo.first_name && senderInfo.first_name !== "Customer") {
          setUserDataOverrides(prev => ({
            ...prev,
            [otherUserId]: {
              first_name: senderInfo.first_name,
              last_name: senderInfo.last_name || "",
              avatar: senderInfo.avatar || ""
            }
          }));
        }
      }

      // Refresh chat users list if this is a new conversation
      const userExists = get_all_chats_data?.some(u => u.id === otherUserId);
      if (!userExists) {
        dispatch(getChatUsersListAsync());
      }

      // Mark as read if chat is open
      if (selectedUser?.id === data.senderId) {
        socket.emit("mark-messages-read", {
          roomId,
          userId: user.id,
          companyId: "66c7dcd95dbdcd5ccb67432a",
        });
      } else {
        // Increment unread count in Redux
        dispatch(incrementUserUnread(data.senderId));
      }
    });

    // Listen for typing indicator
    socket.on("user-typing", (data) => {
      if (data.userId !== user.id && selectedUser?.id === data.userId) {
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 3000);

        // Update last seen when someone is typing
        setUserLastSeen(prev => ({
          ...prev,
          [data.userId]: Date.now()
        }));
      }
    });

    // Listen for online users
    socket.on("online-users", (users) => {
      const userIds = Array.isArray(users) ? users : (users?.users || []);
      setOnlineUsers(userIds);
    });

    // Listen for message edited
    socket.on("message-edited", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId ? { ...msg, message: data.newMessage, edited: true } : msg
        )
      );
    });

    // Listen for message deleted
    socket.on("message-deleted", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId ? { ...msg, deleted: true } : msg
        )
      );
    });

    return () => {
      socket.off("previous-messages");
      socket.off("last-messages");
      socket.off("receive-message");
      socket.off("user-typing");
      socket.off("online-users");
      socket.off("message-edited");
      socket.off("message-deleted");
    };
  }, [user, selectedUser, roomId]);

  // Move user to top of recent conversations
  const moveToTop = (userId) => {
    setRecentConversations(prev => {
      const filtered = prev.filter(id => id !== userId);
      return [userId, ...filtered];
    });
  };

  // Sorted chat list with overridden user data
  const sortedChatUsers = useMemo(() => {
    if (!get_all_chats_data) return [];

    // Apply user data overrides to fix "Customer" placeholders
    const usersWithOverrides = get_all_chats_data.map(user => {
      if (userDataOverrides[user.id]) {
        return {
          ...user,
          ...userDataOverrides[user.id]
        };
      }
      return user;
    });

    const sorted = [...usersWithOverrides].sort((a, b) => {
      const indexA = recentConversations.indexOf(a.id);
      const indexB = recentConversations.indexOf(b.id);

      // If both in recent list, sort by position
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      // If only A in recent list, A comes first
      if (indexA !== -1) return -1;
      // If only B in recent list, B comes first
      if (indexB !== -1) return 1;
      // Neither in recent list, keep original order
      return 0;
    });

    return sorted;
  }, [get_all_chats_data, recentConversations, userDataOverrides]);

  // Create room ID
  const createRoomId = (userId1, userId2) => {
    const ids = [Number(userId1), Number(userId2)].sort((a, b) => a - b);
    return `${ids[0]}-${ids[1]}`;
  };

  // Select user and load conversation
  const handleSelectUser = (chatUser) => {
    // Leave previous room if exists
    if (roomId) {
      socket.emit("leave-room", roomId);
    }

    setSelectedUser(chatUser);
    setMessages([]);

    // Move this conversation to the top
    moveToTop(chatUser.id);

    const newRoomId = createRoomId(user.id, chatUser.id);
    setRoomId(newRoomId);

    // Clear unread count in Redux
    dispatch(clearUserUnread(chatUser.id));

    // Join room first
    socket.emit("join-room", newRoomId);

    // Wait a moment for room join to complete, then fetch messages
    setTimeout(() => {
      socket.emit("fetch-messages", {
        roomId: newRoomId,
        companyId: "66c7dcd95dbdcd5ccb67432a",
      });

      // Mark as read
      socket.emit("mark-messages-read", {
        roomId: newRoomId,
        userId: user.id,
        companyId: "66c7dcd95dbdcd5ccb67432a",
      });
    }, 300);
  };

  // Check if user is online (frontend-only tracking)
  // Users show as offline by default, only online when actively messaging in current session
  const isUserOnline = (userId) => {
    const lastSeen = userLastSeen[userId];
    if (!lastSeen) return false;

    // Consider online if active in last 5 minutes
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    return lastSeen > fiveMinutesAgo;
  };

  // Handle typing indicator
  const handleTyping = (e) => {
    setInputMessage(e.target.value);

    // Emit typing event
    if (roomId && selectedUser) {
      socket.emit("typing", {
        roomId,
        userId: user.id,
        companyId: "66c7dcd95dbdcd5ccb67432a",
      });

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Stop typing after 2 seconds
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("stop-typing", {
          roomId,
          userId: user.id,
          companyId: "66c7dcd95dbdcd5ccb67432a",
        });
      }, 2000);
    }
  };

  // Send message
  const handleSendMessage = () => {
    if (!inputMessage.trim() || !roomId || !selectedUser) return;

    if (editingMessage) {
      // Edit existing message
      socket.emit("edit-message", {
        messageId: editingMessage._id,
        newMessage: inputMessage.trim(),
        roomId,
        companyId: "66c7dcd95dbdcd5ccb67432a",
      });
      setEditingMessage(null);
    } else {
      // Send new message
      const messageData = {
        message: inputMessage.trim(),
        roomId,
        senderId: user.id,
        receiverId: selectedUser.id,
        createdAt: new Date().toISOString(),
        companyId: "66c7dcd95dbdcd5ccb67432a",
      };

      // Add message immediately (optimistic update)
      setMessages((prev) => [...prev, messageData]);

      // Move conversation to top
      moveToTop(selectedUser.id);

      // Don't mark recipient as online just because WE sent them a message
      // Only mark as online when THEY send a message or are typing

      // Send via socket
      socket.emit("message", messageData);

      // Send email notification to recipient
      sendMessageNotification(selectedUser.id, inputMessage.trim());
    }

    setInputMessage("");
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !roomId || !selectedUser) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("roomId", roomId);
    formData.append("senderId", user.id);
    formData.append("receiverId", selectedUser.id);

    // TODO: Upload file to server
    console.log("📎 File upload:", file.name);
  };

  // Delete message
  const handleDeleteMessage = (message) => {
    socket.emit("delete-message", {
      messageId: message._id,
      roomId,
      companyId: "66c7dcd95dbdcd5ccb67432a",
    });
    setMessageMenuAnchor(null);
  };

  // Edit message
  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setInputMessage(message.message);
    setMessageMenuAnchor(null);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Box sx={{ height: "calc(100vh - 130px)", px: { xs: 1, sm: 3 }, pb: 2 }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ py: 2.5 }}>
        <Typography sx={{ fontSize: "26px", fontWeight: "500", color: "#333", fontFamily: "Raleway" }}>
          My{" "}
          <Typography component="span" sx={{ fontSize: "26px", fontWeight: "700", color: "#333", fontFamily: "Raleway" }}>
            Messages
          </Typography>
        </Typography>
        {sortedChatUsers.length > 0 && (
          <Chip
            label={sortedChatUsers.length}
            size="small"
            sx={{ backgroundColor: `${themeOrange}15`, color: themeOrange, fontWeight: "700", fontSize: "12px", border: `1px solid ${themeOrange}40` }}
          />
        )}
      </Stack>

      <Box
        sx={{
          height: "calc(100% - 70px)",
          borderRadius: "16px",
          border: "1.5px solid #ebebeb",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.07)",
          backgroundColor: "white",
        }}
      >
        <Grid container sx={{ height: "100%" }}>
          {/* ── Contact list ── */}
          <Grid
            item
            xs={12}
            md={3.5}
            sx={{
              borderRight: "1.5px solid #f0f0f0",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              display: { xs: selectedUser ? "none" : "flex", md: "flex" },
            }}
          >
            {/* List header */}
            <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f0f0f0" }}>
              <Typography sx={{ fontWeight: "700", fontSize: "15px", color: "#1a1a1a" }}>
                Conversations
              </Typography>
            </Box>

            {/* Contact rows */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {sortedChatUsers.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", p: 3 }} spacing={1}>
                  <Typography sx={{ color: "#ccc", fontSize: "14px", textAlign: "center" }}>
                    No conversations yet
                  </Typography>
                </Stack>
              ) : (
                sortedChatUsers.map((chatUser) => {
                  const hasUnread = unreadCounts[chatUser.id] > 0;
                  const isActive = selectedUser?.id === chatUser.id;
                  const online = isUserOnline(chatUser.id);
                  return (
                    <Box
                      key={chatUser.id}
                      onClick={() => handleSelectUser(chatUser)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        backgroundColor: isActive
                          ? `${themeOrange}10`
                          : hasUnread
                          ? "#fff8f5"
                          : "white",
                        borderBottom: "1px solid #f5f5f5",
                        borderLeft: isActive
                          ? `3px solid ${themeOrange}`
                          : hasUnread
                          ? `3px solid ${themeOrange}80`
                          : "3px solid transparent",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: isActive ? `${themeOrange}10` : "#fafafa",
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            variant="dot"
                            sx={{
                              "& .MuiBadge-badge": {
                                backgroundColor: online ? "#22c55e" : "#d1d5db",
                                width: 11,
                                height: 11,
                                borderRadius: "50%",
                                border: "2px solid white",
                              },
                            }}
                          >
                            <Avatar
                              src={chatUser.avatar ? `${imgUrl}${chatUser.avatar}` : ""}
                              sx={{
                                width: 44,
                                height: 44,
                                backgroundColor: "#071d5b",
                                fontWeight: "700",
                                fontSize: "16px",
                              }}
                            >
                              {chatUser.first_name?.[0]}
                            </Avatar>
                          </Badge>
                          <Box>
                            <Typography sx={{ fontWeight: hasUnread ? "700" : "600", fontSize: "14px", color: "#1a1a1a" }}>
                              {chatUser.first_name} {chatUser.last_name}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: online ? "#22c55e" : "#9ca3af", fontWeight: "500" }}>
                              {online ? "Online" : "Offline"}
                            </Typography>
                          </Box>
                        </Stack>
                        {hasUnread && (
                          <Box sx={{ width: 20, height: 20, borderRadius: "50%", backgroundColor: themeOrange, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: "11px", color: "white", fontWeight: "700", lineHeight: 1 }}>
                              {unreadCounts[chatUser.id]}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Box>
          </Grid>

          {/* ── Chat area ── */}
          <Grid
            item
            xs={12}
            md={8.5}
            sx={{
              height: "100%",
              display: { xs: selectedUser ? "flex" : "none", md: "flex" },
              flexDirection: "column",
            }}
          >
            {selectedUser ? (
              <>
                {/* Chat header */}
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderBottom: "1.5px solid #f0f0f0",
                    background: "linear-gradient(135deg, #fff 0%, #fafafa 100%)",
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <IconButton
                      onClick={() => setSelectedUser(null)}
                      sx={{ display: { xs: "flex", md: "none" }, color: themeOrange, p: 0.75 }}
                    >
                      <IoMdArrowBack size={22} />
                    </IconButton>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      variant="dot"
                      sx={{
                        "& .MuiBadge-badge": {
                          backgroundColor: isUserOnline(selectedUser.id) ? "#22c55e" : "#d1d5db",
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          border: "2px solid white",
                        },
                      }}
                    >
                      <Avatar
                        src={selectedUser.avatar ? `${imgUrl}${selectedUser.avatar}` : ""}
                        sx={{ width: 44, height: 44, backgroundColor: "#071d5b", fontWeight: "700", fontSize: "16px" }}
                      >
                        {selectedUser.first_name?.[0]}
                      </Avatar>
                    </Badge>
                    <Box>
                      <Typography sx={{ fontWeight: "700", fontSize: "16px", color: "#1a1a1a" }}>
                        {selectedUser.first_name} {selectedUser.last_name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: isTyping ? themeOrange : isUserOnline(selectedUser.id) ? "#22c55e" : "#9ca3af", fontWeight: "500" }}>
                        {isTyping ? "typing..." : isUserOnline(selectedUser.id) ? "Online" : "Offline"}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Messages area */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    px: 3,
                    py: 2,
                    backgroundColor: "#f9f9fb",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {messages.map((msg, index) => {
                    const isMine = Number(msg.senderId) === Number(user.id);
                    return (
                      <Stack
                        key={index}
                        direction="row"
                        justifyContent={isMine ? "flex-end" : "flex-start"}
                        sx={{ mb: 0.5 }}
                      >
                        {!isMine && (
                          <Avatar
                            src={selectedUser.avatar ? `${imgUrl}${selectedUser.avatar}` : ""}
                            sx={{ width: 28, height: 28, mr: 1, mt: "auto", mb: 0.5, fontSize: "12px", backgroundColor: "#071d5b", flexShrink: 0 }}
                          >
                            {selectedUser.first_name?.[0]}
                          </Avatar>
                        )}
                        <Box sx={{ maxWidth: "65%" }}>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.25,
                              borderRadius: isMine ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                              backgroundColor: msg.deleted
                                ? "#f3f4f6"
                                : isMine
                                ? `linear-gradient(135deg, ${themeOrange} 0%, #c53f10 100%)`
                                : "white",
                              background: msg.deleted
                                ? "#f3f4f6"
                                : isMine
                                ? `linear-gradient(135deg, ${themeOrange} 0%, #c53f10 100%)`
                                : "white",
                              color: msg.deleted ? "#9ca3af" : isMine ? "white" : "#1a1a1a",
                              boxShadow: isMine
                                ? "0 2px 8px rgba(241,90,36,0.25)"
                                : "0 1px 4px rgba(0,0,0,0.08)",
                              border: isMine ? "none" : "1px solid #f0f0f0",
                            }}
                          >
                            <Stack direction="row" alignItems="center" gap={0.5}>
                              <Typography sx={{ fontSize: "14px", fontStyle: msg.deleted ? "italic" : "normal", lineHeight: 1.45 }}>
                                {msg.deleted ? "This message was deleted" : msg.message || msg.content}
                              </Typography>
                              {isMine && !msg.deleted && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => { setSelectedMessage(msg); setMessageMenuAnchor(e.currentTarget); }}
                                  sx={{ p: 0.25, ml: 0.5, opacity: 0.7, "&:hover": { opacity: 1 } }}
                                >
                                  <MdMoreVert size={14} color="white" />
                                </IconButton>
                              )}
                            </Stack>
                            {msg.edited && !msg.deleted && (
                              <Typography sx={{ fontSize: "9px", mt: 0.25, opacity: 0.6, fontStyle: "italic" }}>
                                edited
                              </Typography>
                            )}
                            <Stack direction="row" alignItems="center" gap={0.5} justifyContent="flex-end" sx={{ mt: 0.5 }}>
                              <Typography sx={{ fontSize: "10px", opacity: 0.65 }}>
                                {formatTime(msg.createdAt)}
                              </Typography>
                              {isMine && !msg.deleted && (
                                msg.read
                                  ? <MdDoneAll size={13} style={{ opacity: 0.75 }} />
                                  : <MdCheck size={13} style={{ opacity: 0.75 }} />
                              )}
                            </Stack>
                          </Box>
                        </Box>
                      </Stack>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Input bar */}
                <Box sx={{ px: 2.5, py: 2, borderTop: "1.5px solid #f0f0f0", backgroundColor: "white" }}>
                  {editingMessage && (
                    <Box sx={{ mb: 1.5, px: 2, py: 1, backgroundColor: `${themeOrange}10`, borderRadius: "8px", border: `1px solid ${themeOrange}30`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <MdEdit size={14} color={themeOrange} />
                        <Typography sx={{ fontSize: "13px", color: themeOrange, fontWeight: "500" }}>
                          Editing message
                        </Typography>
                      </Stack>
                      <Button size="small" onClick={() => { setEditingMessage(null); setInputMessage(""); }} sx={{ color: themeOrange, textTransform: "none", fontSize: "12px", p: 0.5 }}>
                        Cancel
                      </Button>
                    </Box>
                  )}
                  <Stack direction="row" alignItems="flex-end" gap={1}>
                    <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
                    <Stack direction="row" gap={0.5}>
                      <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={{ color: "#9ca3af", "&:hover": { color: themeOrange } }}>
                        <IoMdAttach size={20} />
                      </IconButton>
                      <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={{ color: "#9ca3af", "&:hover": { color: themeOrange } }}>
                        <IoMdImage size={20} />
                      </IconButton>
                    </Stack>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      value={inputMessage}
                      onChange={handleTyping}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      variant="outlined"
                      size="small"
                      multiline
                      maxRows={4}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "#f9f9fb",
                          fontSize: "14px",
                          "&:hover fieldset": { borderColor: `${themeOrange}60` },
                          "&.Mui-focused fieldset": { borderColor: themeOrange },
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!inputMessage.trim()}
                      sx={{
                        width: 42,
                        height: 42,
                        background: inputMessage.trim()
                          ? `linear-gradient(135deg, ${themeOrange} 0%, #c53f10 100%)`
                          : "#e5e7eb",
                        color: "white",
                        flexShrink: 0,
                        borderRadius: "12px",
                        transition: "all 0.2s",
                        "&:hover": {
                          background: inputMessage.trim()
                            ? `linear-gradient(135deg, #d94d1a 0%, #b3370d 100%)`
                            : "#e5e7eb",
                        },
                      }}
                    >
                      <IoIosSend size={20} />
                    </IconButton>
                  </Stack>
                </Box>
              </>
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }} spacing={2}>
                <Box sx={{ width: 72, height: 72, borderRadius: "50%", backgroundColor: `${themeOrange}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IoIosSend size={30} color={themeOrange} style={{ transform: "rotate(-30deg)" }} />
                </Box>
                <Typography sx={{ color: "#9ca3af", fontSize: "16px", fontWeight: "500" }}>
                  Select a conversation to start messaging
                </Typography>
              </Stack>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Message context menu */}
      <Menu
        anchorEl={messageMenuAnchor}
        open={Boolean(messageMenuAnchor)}
        onClose={() => setMessageMenuAnchor(null)}
        PaperProps={{ sx: { borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", border: "1px solid #f0f0f0", minWidth: 140 } }}
      >
        <MenuItem onClick={() => handleEditMessage(selectedMessage)} sx={{ fontSize: "14px", gap: 1, py: 1.25 }}>
          <MdEdit size={16} color={themeOrange} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteMessage(selectedMessage)} sx={{ fontSize: "14px", color: "#ef4444", gap: 1, py: 1.25 }}>
          <MdDelete size={16} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SimpleMessages;
